import { useEffect, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { useOverlay } from "@/Common/CustomHooks/useOverlay";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PopulationBasedFundingStatusUIModel } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingStatusUIModel";
import { PopulationBasedFundingPortfolioFilterSettings } from "@/Portfolio/Models/Settings/PopulationBasedFundingTableFilterSettings";
import { PopulationBasedFundingPortfolioActions } from "@/Portfolio/Services/PopulationBasedFundingPortfolioService";

import styles from "./PopulationBasedFundingPortfolioSettings.module.scss";

interface PopulationBasedFundingPortfolioSettingsProps {
    onClose: () => void;
    filterSettings: PopulationBasedFundingPortfolioFilterSettings;
    onColumnOrderingPopupOpenClick: () => void;
}

function PopulationBasedFundingPortfolioSettings(props: PopulationBasedFundingPortfolioSettingsProps): JSX.Element {
    useOverlay(
        "PopulationBasedFundingPortfolioSettings",
        { transparent: true, onClicked: props.onClose }
    );

    const { AvailableClinicians, AvailableStatus, AvailableSites } = portfolioStore.populationBasedFundingPortfolioModel;
    const [availableCliniciansFromSiteFilter, setAvailableCliniciansFromFilters] = useState(AvailableClinicians);

    const { getText } = useTranslation();
    const [isSaveSettingsChecked, setIsSaveSettingsChecked] =
        useState(false);

    const [filterSettings, setFilterSettings] = useState({ ...props.filterSettings });
    const [queryPageSize, setQueryPageSize] = useState(props.filterSettings.QueryPageSize);

    useEffect(() => {
        const selectedSitesIdsDictionary = filterSettings?.IncludedSites.distinct().toDictionary(p => p);
        let filteredClinicianBySiteAndName = AvailableClinicians;

        if (!!selectedSitesIdsDictionary && Object.keys(selectedSitesIdsDictionary).length) {
            filteredClinicianBySiteAndName = filteredClinicianBySiteAndName.filter(p => p.Sites.any(site => !!selectedSitesIdsDictionary[site]));
        }

        if (!IsNullOrWhiteSpace(filterSettings?.ClinicianSearchText)) {
            filteredClinicianBySiteAndName = filteredClinicianBySiteAndName.filterSearch(filterSettings?.ClinicianSearchText, p => p.Name);
        }

        setAvailableCliniciansFromFilters(filteredClinicianBySiteAndName);
    }, [filterSettings?.ClinicianSearchText, filterSettings?.IncludedSites, AvailableClinicians]);

    const setSelectSite = (isChecked: boolean, site: SiteUIModel): void => {
        const nextSites = isChecked
            ? [...filterSettings.IncludedSites, site.Id].distinct()
            : filterSettings.IncludedSites.filter(siteId => siteId != site.Id);
        setFilterSettings({ ...filterSettings, IncludedSites: nextSites });
    };
    const setSelectAllSites = (isChecked: boolean): void => {
        const nextSites = isChecked
            ? AvailableSites.map(site => site.Id)
            : [];
        setFilterSettings({ ...filterSettings, IncludedSites: nextSites });
    };

    const setClinicianSearchText = (searchText: string): void => {
        setFilterSettings({ ...filterSettings, ClinicianSearchText: searchText });
    };
    const setSelectClinician = (isChecked: boolean, clinician: EmployeeUIModel): void => {
        const nextClinicians = isChecked
            ? [...filterSettings.IncludedEmployeeContactFacetIds, clinician.ContactFacetId].distinct()
            : filterSettings.IncludedEmployeeContactFacetIds.filter(clinicianId => clinicianId != clinician.ContactFacetId);

        setFilterSettings({ ...filterSettings, IncludedEmployeeContactFacetIds: nextClinicians });
    };
    const setSelectAllClinicians = (isChecked: boolean): void => {
        const nextClinicians = isChecked
            ? availableCliniciansFromSiteFilter.map(clinician => clinician.ContactFacetId)
            : [];

        setFilterSettings({ ...filterSettings, IncludedEmployeeContactFacetIds: nextClinicians });
    };

    const setSelectStatus = (isChecked: boolean, status: PopulationBasedFundingStatusUIModel): void => {
        const nextStatuses = isChecked
            ? [...filterSettings.IncludedStatuses, status.Enum].distinct()
            : filterSettings.IncludedStatuses.filter(statusId => statusId != status.Enum);

        setFilterSettings({ ...filterSettings, IncludedStatuses: nextStatuses });
    };

    const setSelectAllStatuses = (isChecked: boolean): void => {
        const nextStatuses = isChecked
            ? AvailableStatus.map(status => status.Enum)
            : [];

        setFilterSettings({ ...filterSettings, IncludedStatuses: nextStatuses });
    };

    const setShowEntriesWithNoAssignedPhysician = (showEntriesWithNoAssignedPhysician: boolean): void => {
        setFilterSettings({ ...filterSettings, ShowEntriesWithNoAssignedPhysician: showEntriesWithNoAssignedPhysician });
    };

    return (
        <div className={styles.populationBasedFundingPortfolioSettings}>
            <div
                style={{ gridArea: "siteHeader" }}
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("SitesLabel")}
                    checked={filterSettings.IncludedSites?.length === AvailableSites.length}
                    onChange={isChecked => setSelectAllSites(isChecked)}
                />
            </div>
            <div
                style={{ gridArea: "clinicianHeader" }}
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("CliniciansLabel")}
                    checked={filterSettings.IncludedEmployeeContactFacetIds?.length === availableCliniciansFromSiteFilter.length}
                    onChange={isChecked => setSelectAllClinicians(isChecked)}
                />
            </div>
            <div
                style={{ gridArea: "statusHeader" }}
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("StatusLabel")}
                    checked={filterSettings.IncludedStatuses?.length === AvailableStatus.length}
                    onChange={isChecked => setSelectAllStatuses(isChecked)}
                />
            </div>

            <div style={{ gridArea: "siteColumn" }} className={`${styles.siteColumn} ${styles.column}`}>
                {AvailableSites.map(site => (
                    <CheckboxControl
                        id={`site_${site.Id}`}
                        label={site.Name}
                        checked={filterSettings.IncludedSites?.contains(site.Id)}
                        onChange={isChecked => setSelectSite(isChecked, site)}
                        key={`site_${site.Id}`}
                        DivWrap={true}
                    />
                ))}
            </div>

            <div style={{ gridArea: "clinicianColumn" }} className={`${styles.clinicianColumn} ${styles.column}`}>
                <TextboxControl
                    id="clinicians_search_text"
                    value={filterSettings.ClinicianSearchText}
                    onChange={(text: string) => setClinicianSearchText(text)}
                    className="form-control"
                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                    key="clinicians_search_text"
                />

                <CheckboxControl
                    id="clinicians_show_unknown"
                    label={getText("NoAssignedPhysicianLabel")}
                    checked={filterSettings.ShowEntriesWithNoAssignedPhysician}
                    onChange={isChecked => setShowEntriesWithNoAssignedPhysician(isChecked)}
                    key="clinicians_show_unknown"
                />
                <div className={styles.cliniciansList}>
                    {availableCliniciansFromSiteFilter.map(clinician => (
                        <CheckboxControl
                            id={`clinicians_${clinician.Id}`}
                            label={clinician.Name}
                            checked={filterSettings.IncludedEmployeeContactFacetIds?.contains(clinician.ContactFacetId)}
                            onChange={isChecked => setSelectClinician(isChecked, clinician)}
                            key={`clinicians_${clinician.Id}`}
                        />
                    ))}
                </div>
            </div>

            <div style={{ gridArea: "statusColumn" }} className={styles.column}>
                {AvailableStatus.map(status => (
                    <CheckboxControl
                        id={`status_${status.Enum}`}
                        label={getText(status.VocabKey)}
                        checked={filterSettings.IncludedStatuses?.contains(status.Enum)}
                        onChange={isChecked => setSelectStatus(isChecked, status)}
                        key={`status_${status.Enum}`}
                    />
                ))}
            </div>

            <div className={styles.footer} style={{ gridArea: "footer" }}>
                <SearchSettingsFooter
                    onClose={props.onClose}
                    onOk={() =>
                        PopulationBasedFundingPortfolioActions.saveFilterSettings(
                            filterSettings,
                            isSaveSettingsChecked,
                            props.onClose
                        )
                    }
                    onColumnOrderingButtonClick={() => props.onColumnOrderingPopupOpenClick()}
                    onResultsPerPageChange={pageSize => setQueryPageSize(pageSize)}
                    selectedPageSizeOption={queryPageSize}
                    onSaveSettingsCheckboxChange={checked => setIsSaveSettingsChecked(checked)}
                    isSaveSettingsChecked={isSaveSettingsChecked}
                    pageSizeOptions={[30, 50, 100]}
                />
            </div>
        </div>
    );
}
export { PopulationBasedFundingPortfolioSettings };
